"use client";

import CatchPhrase from "@/components/index/fragments/CatchPhrase";
import AdminWatchDogPrices from "@/components/index/fragments/AdminWatchDogPrices";
import { HomePortfolioSummary } from "@/components/index/fragments/HomePortfolioSummary";
import { HomeWatchDogSummary } from "@/components/index/fragments/HomeWatchDogSummary";
import { HomeUserActivity } from "@/components/index/fragments/HomeUserActivity";
import { HomeUpcomingPortfolioSummary } from "@/components/index/fragments/HomeUpcomingPortfolioSummary";
import LoginForm from "@/components/index/fragments/LoginForm";
import Membership from "@/components/index/fragments/Memberhip";
import { AppPageIntroSlot } from "@/components/layout/AppPageIntroSlot";
import { Button } from "@/components/ui/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/Tooltip";
import { getSiteContent } from "@/content/site";
import { useAdminContext } from "@/context/AdminContext";
import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import { useSessionContext } from "@/context/SessionContext";
import { HiddenHomeSectionPreference } from "@/types";
import { ChevronDoubleDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import LoadingHero from "@/components/index/fragments/LoadingHero";

const Hero = () => {
  const { isInitialLoading, isPendingRegistration, isPaymentLoading } =
    useSessionContext();
  const { isLoggedIn, userPreferences, updateUserPreferences } =
    useAppContext();
  const { isAdmin } = useAdminContext();
  const { language } = useLanguage();
  const [rankedQuestionStartIndex, setRankedQuestionStartIndex] = useState(0);
  const { heroBadges } = getSiteContent(language);
  const copy = {
    cs: {
      startFree: "Základní členství zdarma",
      pricingAria: "Přejít na ceník",
      hideWatchdog: "Skrýt přehled hlídacího psa",
      hidePortfolio: "Skrýt přehled portfolia",
    },
    en: {
      startFree: "Basic membership for free",
      pricingAria: "Go to pricing",
      hideWatchdog: "Hide the watchdog overview",
      hidePortfolio: "Hide the portfolio overview",
    },
  }[language];

  useEffect(() => {
    setRankedQuestionStartIndex(Math.floor(Math.random() * 6));
  }, []);

  const showWatchdog = !userPreferences.hideSection.includes("watchdog");
  const showPortfolio = !userPreferences.hideSection.includes("portfolio");
  const hideHomeSection = (section: HiddenHomeSectionPreference) => {
    const otherSection = section === "watchdog" ? "portfolio" : "watchdog";
    if (userPreferences.hideSection.includes(otherSection)) return;

    void updateUserPreferences({
      hideSection: userPreferences.hideSection.includes(section)
        ? userPreferences.hideSection
        : [...userPreferences.hideSection, section],
    });
  };
  const hideAction = (section: HiddenHomeSectionPreference, label: string) => (
    <div className="inline-flex flex-col items-center gap-1">
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="unstyled"
              onClick={() => hideHomeSection(section)}
              aria-label={label}
              className="flex size-10 items-center justify-center rounded-none rounded-bl-2xl border-b border-l border-softiq-primary/15 bg-softiq-surface-cool text-softiq-primary/50 hover:bg-white hover:text-softiq-primary dark:border-white/15 dark:bg-softiq-dark-surface dark:text-white/50 dark:hover:bg-softiq-dark-elevated dark:hover:text-white"
            >
              <XMarkIcon className="size-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={10}>
            {label}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );

  if (isLoggedIn) {
    return (
      <>
        <section className="app-page-section overflow-visible">
          <div className="app-page-container">
            <AppPageIntroSlot
              showProfile
              isProfileLoading={isInitialLoading || isPaymentLoading}
              hiddenTargets={[
                ...(showWatchdog ? [] : ["dashboard-watchdog"]),
                ...(showPortfolio ? [] : ["dashboard-portfolio"]),
              ]}
            />

            <div className="app-page-main-grid">
              {showPortfolio && (
                <div
                  data-tour="dashboard-portfolio"
                  className={`min-w-0 self-start ${showWatchdog ? "" : "xl:col-span-2"}`}
                >
                  <HomePortfolioSummary
                    compact
                    headerAction={
                      showWatchdog
                        ? hideAction("portfolio", copy.hidePortfolio)
                        : undefined
                    }
                  />
                </div>
              )}
              {showWatchdog && (
                <div
                  data-tour="dashboard-watchdog"
                  className={`min-w-0 self-start ${showPortfolio ? "" : "xl:col-span-2"}`}
                >
                  <HomeWatchDogSummary
                    headerAction={
                      showPortfolio
                        ? hideAction("watchdog", copy.hideWatchdog)
                        : undefined
                    }
                  />
                </div>
              )}
              {showPortfolio ? (
                <div
                  data-tour="dashboard-upcoming-portfolio"
                  className="min-w-0 xl:col-span-2"
                >
                  <HomeUpcomingPortfolioSummary />
                </div>
              ) : null}
              <div
                data-tour="dashboard-activity"
                className="min-w-0 xl:col-span-2"
              >
                <HomeUserActivity />
              </div>
            </div>
          </div>
        </section>

        {isAdmin && <section className="h-[100vh]" aria-hidden="true" />}
        {isAdmin && (
          <AdminWatchDogPrices
            offerType="all"
            questionStartIndex={rankedQuestionStartIndex}
          />
        )}
      </>
    );
  }

  return (
    <div className="hero-root">
      <div className="hero-inner">
        <div className="hero-left">
          <CatchPhrase />
          <div className="hero-badges">
            <div className="hero-badges-track">
              {Array.from({ length: 2 }).map((_, groupIndex) => (
                <div
                  key={groupIndex}
                  className="hero-badges-group"
                  aria-hidden={groupIndex === 1}
                >
                  {heroBadges.map((badge, index) => (
                    <span
                      key={`${badge}-${groupIndex}-${index}`}
                      className="hero-badge"
                    >
                      {badge}
                    </span>
                  ))}
                  <span className="hero-badge-spacer" aria-hidden="true" />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="hero-right">
          <div className="hero-card" id="prihlaseni">
            <div className="flex flex-1 flex-col justify-center">
              <div className="hero-card-eyebrow">
                <Image
                  src="/image/paw/paw-solid.svg"
                  width={1000}
                  height={1000}
                  className="size-4 opacity-80 hero-card-dot"
                  alt=""
                  aria-hidden="true"
                />
                {copy.startFree}
              </div>
              {(isInitialLoading || isPaymentLoading) && <LoadingHero />}
              {!isInitialLoading &&
                !isPaymentLoading &&
                isPendingRegistration && <Membership />}
              {!isInitialLoading &&
                !isPaymentLoading &&
                !isPendingRegistration && <LoginForm />}
            </div>
          </div>
        </div>
      </div>
      {isAdmin && (
        <AdminWatchDogPrices
          offerType="all"
          questionStartIndex={rankedQuestionStartIndex}
        />
      )}
      {!isLoggedIn && (
        <div className="hero-footer">
          <Link
            href="/#cenik"
            aria-label={copy.pricingAria}
            className="hero-scroll-cue"
          >
            <ChevronDoubleDownIcon className="hero-chevron" />
          </Link>
        </div>
      )}
    </div>
  );
};

export default Hero;
